import { useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { Sparkles, ImageIcon, ChevronDown, ChevronUp, X, AlertCircle, Upload } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { supabase } from '@/lib/supabase';

const ENTITY_TABLE = {
  chapter: 'chapters',
  exercise: 'exercises',
  epreuve: 'epreuves',
  objective_template: 'objective_templates',
  theme: 'themes',
  live_campaign: 'live_campaigns',
} as const;

const MAX_UPLOAD_SIZE = 5 * 1024 * 1024; // 5 Mo (aligné sur la policy du bucket)
const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp'];

interface IllustrationEditorProps {
  entityType: 'chapter' | 'exercise' | 'epreuve' | 'objective_template' | 'theme' | 'live_campaign';
  entityId: string;
  initialUrl?: string | null;
  initialPrompt?: string | null;
  subject?: string;
  level?: string;
  title?: string;
  /** Libellé de l'en-tête (défaut « Illustration »). Ex. « Couverture ». */
  label?: string;
}

export function IllustrationEditor({
  entityType,
  entityId,
  initialUrl,
  initialPrompt,
  subject,
  level,
  title,
  label = 'Illustration',
}: IllustrationEditorProps) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState(initialPrompt ?? '');
  const [url, setUrl] = useState<string | null>(initialUrl ?? null);
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleGenerate() {
    if (!prompt.trim()) return;
    setGenerating(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('generate-illustration', {
        body: {
          prompt: prompt.trim(),
          entity_type: entityType,
          entity_id: entityId,
          subject,
          level,
          title,
        },
      });

      if (fnError) {
        // Le body de la réponse HTTP porte un message déjà nettoyé par l'edge function.
        let message = fnError.message;
        try {
          const body = await (
            fnError as { context?: { json?: () => Promise<unknown> } }
          ).context?.json?.();
          const errText = (body as { error?: unknown } | undefined)?.error;
          if (typeof errText === 'string' && errText.trim()) message = errText;
        } catch {
          // garde le message générique
        }
        throw new Error(message);
      }

      if (data?.url) setUrl(data.url);
      else throw new Error("Pas d'URL dans la réponse.");
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inattendue.');
    } finally {
      setGenerating(false);
    }
  }

  async function handleUpload(file: File) {
    setError(null);

    if (!ALLOWED_MIME.includes(file.type)) {
      setError('Format non supporté. Utilise JPEG, PNG ou WebP.');
      return;
    }
    if (file.size > MAX_UPLOAD_SIZE) {
      setError('Fichier trop volumineux (max 5 Mo).');
      return;
    }

    setUploading(true);
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? 'png';
      const fileName = `${entityType}/${entityId}/${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('illustrations')
        .upload(fileName, file, { contentType: file.type, upsert: true });
      if (uploadError) throw new Error(uploadError.message);

      const { data: publicUrlData } = supabase.storage.from('illustrations').getPublicUrl(fileName);
      const publicUrl = publicUrlData.publicUrl;

      // `live_campaigns` est postérieure aux types stale → client générique.
      const { error: updateError } = await (supabase as unknown as SupabaseClient)
        .from(ENTITY_TABLE[entityType])
        .update({ illustration_url: publicUrl })
        .eq('id', entityId);
      if (updateError) throw new Error(updateError.message);

      setUrl(publicUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l’upload.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  return (
    <div className="border-ink-200 overflow-hidden rounded-2xl border bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="hover:bg-ink-50 flex w-full items-center justify-between px-5 py-4 transition-colors"
      >
        <div className="flex items-center gap-2">
          <ImageIcon className="text-ink-500 h-4 w-4" />
          <span className="font-display text-ink-800 text-sm font-bold">{label}</span>
          {url && (
            <span className="bg-primary-100 text-primary-700 rounded-full px-2 py-0.5 font-sans text-[11px] font-medium">
              ✓
            </span>
          )}
        </div>
        {open ? (
          <ChevronUp className="text-ink-400 h-4 w-4" />
        ) : (
          <ChevronDown className="text-ink-400 h-4 w-4" />
        )}
      </button>

      {open && (
        <div className="border-ink-100 space-y-4 border-t p-5">
          {url ? (
            <div className="bg-ink-100 aspect-video overflow-hidden rounded-xl">
              <img src={url} alt="Illustration" className="h-full w-full object-cover" />
            </div>
          ) : (
            <div className="bg-ink-50 flex aspect-video items-center justify-center rounded-xl">
              <p className="text-ink-400 font-sans text-[12px]">Aucune illustration</p>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <label className="font-display text-ink-700 text-[12px] font-semibold">
              Description pour DALL-E
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Ex : une équation linéaire sur fond pastel bleu, style épuré et pédagogique…"
              className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 resize-none rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
            />
            <p className="text-ink-400 text-right font-sans text-[11px]">{prompt.length}/500</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generating || uploading || !prompt.trim()}
              className={cn(
                'font-display inline-flex h-9 items-center gap-2 rounded-xl border px-4 text-sm font-bold transition-all active:scale-[.98]',
                generating || uploading || !prompt.trim()
                  ? 'border-ink-200 bg-ink-50 text-ink-400 cursor-not-allowed'
                  : 'border-primary-200 bg-primary-50 text-primary-700 hover:bg-primary-100',
              )}
            >
              <Sparkles className="h-3.5 w-3.5" />
              {generating ? 'Génération en cours…' : url ? 'Régénérer' : 'Générer'}
            </button>

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={generating || uploading}
              className={cn(
                'font-display inline-flex h-9 items-center gap-2 rounded-xl border px-4 text-sm font-bold transition-all active:scale-[.98]',
                generating || uploading
                  ? 'border-ink-200 bg-ink-50 text-ink-400 cursor-not-allowed'
                  : 'border-ink-200 text-ink-700 hover:bg-ink-50 bg-white',
              )}
            >
              <Upload className="h-3.5 w-3.5" />
              {uploading ? 'Upload en cours…' : 'Uploader une image'}
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleUpload(file);
              }}
            />
          </div>

          {error && (
            <div className="border-accent-100 bg-accent-50 flex items-start gap-2 rounded-xl border px-4 py-3">
              <AlertCircle className="text-accent-600 mt-0.5 h-4 w-4 shrink-0" />
              <p className="text-accent-700 min-w-0 flex-1 font-sans text-[13px]">{error}</p>
              <button
                type="button"
                onClick={() => setError(null)}
                className="text-accent-400 hover:text-accent-700 shrink-0 transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
